import { noop } from 'lodash'
import { useCallback, useEffect, useRef, useState } from 'react'

import { PaginationModel, transformPaginationDto } from '@marketplace-web/shared/pagination-data'
import { UiState } from '@marketplace-web/shared/ui-state'
import {
  InboxConversationListItemDto,
  getInboxConversations,
} from '@marketplace-web/messaging/conversation-messaging-data'
import { useBrazeInboxMessageCards } from '@marketplace-web/braze/braze-feature'

import { DEFAULT_PAGE_NUMBER } from '../../constants'
import { sortConversations } from '../utils/conversation'
import {
  incrementConversationListLoad,
  observeConversationListLoadTime,
} from '../utils/observability'

type Args = {
  perPage: number
  currentPage: number
  setCurrentPage?: (page: number) => void
}

const resolveUiState = (
  apiUiState: UiState,
  isBrazeLoaded: boolean,
  hasLoadedConversations: boolean,
) => {
  if (apiUiState !== UiState.Success) {
    if (hasLoadedConversations && isBrazeLoaded && apiUiState === UiState.Pending) {
      return UiState.Success
    }

    return apiUiState
  }

  if (isBrazeLoaded) return UiState.Success

  return UiState.Pending
}

const useFetchInboxConversations = ({ perPage, currentPage, setCurrentPage = noop }: Args) => {
  const [uiState, setUiState] = useState(UiState.Pending)
  const [conversations, setConversations] = useState<Array<InboxConversationListItemDto>>([])
  const [pagination, setPagination] = useState<PaginationModel>()
  const [websocketUserId, setWebsocketUserId] = useState<string>()

  const isEndReached = useRef(false)
  const loadStartTime = useRef<number>(performance.now())
  const isLoadTimeObserved = useRef(false)

  const { inboxMessageCards, inboxControlMessageCards } = useBrazeInboxMessageCards()

  const isBrazeLoaded = inboxMessageCards !== undefined

  const resolvedUiState = resolveUiState(uiState, isBrazeLoaded, !!conversations.length)
  const mergedConversations =
    resolvedUiState === UiState.Success ? [...conversations, ...(inboxMessageCards ?? [])] : []
  const sortedConversations = sortConversations(mergedConversations).slice(0, perPage * currentPage)

  const resetConversations = useCallback(() => {
    setConversations([])
    setPagination(undefined)
    setCurrentPage(DEFAULT_PAGE_NUMBER)
  }, [setCurrentPage])

  useEffect(() => {
    isEndReached.current =
      !!pagination &&
      sortedConversations.length === pagination.totalEntries + (inboxMessageCards?.length ?? 0)
  })

  useEffect(() => {
    if (isLoadTimeObserved.current) return

    const observeLoadEnd = (state: 'success' | 'failure') => {
      const conversationsListLoadTime = performance.now() - loadStartTime.current

      observeConversationListLoadTime(state, conversationsListLoadTime)

      isLoadTimeObserved.current = true
    }

    if (uiState === UiState.Success) {
      observeLoadEnd('success')
    } else if (uiState === UiState.Failure) {
      observeLoadEnd('failure')
    }
  }, [uiState])

  useEffect(() => {
    const filterConversations = (
      previousConversations: Array<InboxConversationListItemDto>,
      fetchedConversations: Array<InboxConversationListItemDto>,
    ) => {
      const prevConversationsIds = previousConversations.map(conversation => conversation.id)
      const filteredFetchedConversations = fetchedConversations.filter(
        conversation => !prevConversationsIds.includes(conversation.id),
      )

      return previousConversations.concat(filteredFetchedConversations)
    }

    const fetchConversations = async () => {
      setUiState(UiState.Pending)

      const response = await getInboxConversations({
        perPage,
        currentPage,
      })

      if ('errors' in response) {
        setUiState(UiState.Failure)
        incrementConversationListLoad('failure')

        return
      }

      setConversations(previousConversations =>
        filterConversations(previousConversations, response.conversations),
      )

      setPagination(transformPaginationDto(response.pagination))
      setUiState(UiState.Success)
      setWebsocketUserId(response.websocket_user_id)

      incrementConversationListLoad('success')
    }

    if (isEndReached.current) return

    fetchConversations()
  }, [perPage, currentPage])

  return {
    uiState: resolvedUiState,
    conversations: sortedConversations,
    controlConversations: inboxControlMessageCards ?? [],
    pagination,
    isEndReached: isEndReached.current,
    setConversations,
    resetConversations,
    websocketUserId,
  }
}

export default useFetchInboxConversations
